import { protectedRpc } from './rpc.ts'

export interface AttOperator {
  att_operator_id: string
  att_operator_name: string
}

export interface AttFilter {
  filter_id: string
  filter_name: string
}

export interface RawNode {
  id: string
  name?: string
  group_decs?: string
  att_desc?: string
  filters?: AttFilter[]
  att_value?: unknown[]
  available?: boolean
  att_operator?: AttOperator[]
  children?: RawNode[]
}

export interface Attribute {
  /** BARE id — what log_search wants. The tree says `8-51`; search wants `51`. */
  searchId: string
  /** Tree id, e.g. `8-51`. Kept for display and debugging only. */
  treeId: string
  /** Machine name — stable, and what the search reply uses as a column header. */
  name: string
  /** Display name — hand-typed upstream, typos included. */
  desc: string
  groupId: string
  groupName: string
  groupDesc: string
  filters: AttFilter[]
  operators: AttOperator[]
  /** Aggregation buckets this attribute accepts in `period`. */
  periods: string[]
  /** false when the account has no subscription: say "not collected", don't fail. */
  available: boolean
}

/** The tree says `8-51`; log_search wants `51`. */
export function bareId(treeId: string): string {
  const s = String(treeId)
  const dash = s.lastIndexOf('-')
  return dash === -1 ? s : s.slice(dash + 1)
}

const PERIOD_ORDER = ['none', 'hour', 'day', 'week', 'month', 'year']

function periodsOf(node: RawNode): string[] {
  const out: string[] = []
  for (const child of node.children ?? []) {
    // A period node carries neither group_decs nor att_desc.
    if (child.group_decs || child.att_desc) continue
    const n = String(child.name ?? child.id ?? '').toLowerCase()
    if (n) out.push(n)
  }
  return out.sort((a, b) => PERIOD_ORDER.indexOf(a) - PERIOD_ORDER.indexOf(b))
}

/**
 * Flatten the two-level tree to attributes only.
 *
 * A group is NOT an attribute: groups carry `group_decs`, attributes carry
 * `att_desc`. Fuzzy-matching a display name without checking `att_desc` will
 * happily resolve group `6` ("output") and search the wrong thing.
 */
export function flattenAttributes(tree: RawNode[]): Attribute[] {
  const out: Attribute[] = []
  for (const group of tree ?? []) {
    const groupId = String(group.id ?? '')
    const groupName = String(group.name ?? '')
    const groupDesc = String(group.group_decs ?? groupName)
    for (const node of group.children ?? []) {
      if (!node.att_desc) continue // groups and period nodes are not attributes
      out.push({
        searchId: bareId(node.id),
        treeId: String(node.id),
        name: String(node.name ?? ''),
        desc: String(node.att_desc),
        groupId,
        groupName,
        groupDesc,
        filters: node.filters ?? [],
        operators: node.att_operator ?? [],
        periods: periodsOf(node),
        available: node.available !== false,
      })
    }
  }
  return out
}

export async function fetchAttributes(): Promise<Attribute[]> {
  const tree = await protectedRpc<RawNode[]>('log_data.log_attribute_get', {})
  return flattenAttributes(Array.isArray(tree) ? tree : [])
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '')

/**
 * Resolve by name on every fetch — the id space is cloud-side and gets
 * renumbered, so ids must never be hardcoded as constants.
 *
 * Matches `att_desc` first (the display name the doc's tables use), then the
 * stable machine `name`. Both are tried exact-then-normalised, because display
 * names are edited upstream (the live list really does contain
 * "Output 2-wire voltage chanfge (mV)").
 */
export function resolveAttribute(
  attrs: Attribute[],
  wanted: { desc?: string; name?: string },
  prefer: 'desc' | 'name' = 'desc',
): Attribute | null {
  const order: ('desc' | 'name')[] = prefer === 'name' ? ['name', 'desc'] : ['desc', 'name']
  const value = (k: 'desc' | 'name') => (k === 'desc' ? wanted.desc : wanted.name)
  // Exact on both keys before loose on either: a normalised match is a guess,
  // and a guess must never beat an exact hit on the other key.
  for (const pass of ['exact', 'loose'] as const) {
    for (const key of order) {
      const want = value(key)
      if (!want) continue
      const hit =
        pass === 'exact'
          ? attrs.find((a) => a[key] === want)
          : attrs.find((a) => norm(a[key]) === norm(want))
      if (hit) return hit
    }
  }
  return null
}

export interface FilterValue {
  value: string
  label: string
}

/** Valid values for an attribute's filter — populates a dropdown. */
export async function fetchFilterValues(
  interfaceId: number,
  attributeSearchId: string,
  filterId: string,
): Promise<FilterValue[]> {
  const r = await protectedRpc<any>('log_data.log_attribute_filter_value_get', {
    interface_id: interfaceId,
    attribute: attributeSearchId,
    filter: filterId,
  })
  const rows: any[] = Array.isArray(r) ? r : (r?.items ?? r?.values ?? [])
  return rows
    .map((row) => {
      if (row == null) return null
      if (typeof row === 'string' || typeof row === 'number') {
        return { value: String(row), label: String(row) }
      }
      const value = String(row.filter_value ?? row.value ?? row.id ?? '')
      if (!value) return null
      const label = String(row.filter_value_name ?? row.name ?? row.description ?? value)
      return { value, label }
    })
    .filter((v): v is FilterValue => v !== null)
}
